import React, { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sparkles,
  Users,
  UserX,
  Wrench,
  PoundSterling,
  Wallet,
  ArrowDown,
  Mail,
  Phone,
  Ruler,
  TrendingUp,
  Beer,
  Sprout,
  Trees,
  ShoppingBag,
  Tent,
  Dumbbell,
  Loader2,
  CheckCircle2,
  Download,
} from "lucide-react";
import { submitEnquiry } from "../lib/api";

const BRAND = {
  forest: "#114232",
  sage: "#94A596",
  cream: "#FAF7F1",
  bone: "#F2EEE3",
  ink: "#0C2A20",
  terracotta: "#E07856",
  terracottaDeep: "#B8593B",
};

const CONTACT_EMAIL = "sarah@messypups.co.uk";

const VENUE_TYPES = [
  "Pub / gastropub",
  "Farm shop",
  "Garden centre",
  "Country park",
  "Holiday park / campsite",
  "Pet shop",
  "Sports club",
  "Other",
];

const scrollToForm = () => {
  const el = document.getElementById("enquire");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const SalesLanding = () => {
  const [form, setForm] = useState({
    name: "",
    business: "",
    venue_type: "",
    phone: "",
    email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.business.trim() || !form.venue_type || !form.email.trim()) {
      toast.error("Name, business, venue type and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await submitEnquiry(form);
      setSent(true);
      toast.success("Thanks — we'll be in touch shortly");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please email sarah@messypups.co.uk directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: BRAND.cream, color: BRAND.ink }} data-testid="sales-landing-page">
      {/* Top strip — no nav, just brand + contact */}
      <header
        className="border-b border-stone-200"
        style={{ background: "rgba(250,247,241,0.95)" }}
        data-testid="sales-header"
      >
        <div className="max-w-[1200px] mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-circle.png" alt="Messy Pups" className="w-10 h-10 rounded-full" />
            <div
              className="text-lg leading-none"
              style={{ fontFamily: "Chewy, system-ui", color: BRAND.forest }}
            >
              Messy Pups
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/advert"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full transition-colors"
              style={{ background: BRAND.bone, color: BRAND.forest, border: `1px solid ${BRAND.sage}` }}
              data-testid="header-advert-pack"
              aria-label="Download advert pack"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Advert pack</span>
            </Link>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="hidden sm:inline-flex items-center gap-2 text-sm hover:underline"
              style={{ color: BRAND.forest }}
              data-testid="header-email"
            >
              <Mail className="w-4 h-4" /> {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </header>

      {/* 1 — HERO */}
      <section className="relative overflow-hidden" style={{ background: BRAND.bone }}>
        <div
          className="absolute -top-24 -left-24 rounded-full"
          style={{ width: 340, height: 340, background: BRAND.sage, opacity: 0.22 }}
        />
        <div
          className="absolute -bottom-24 -right-24 rounded-full"
          style={{ width: 300, height: 300, background: BRAND.terracotta, opacity: 0.14 }}
        />
        <div className="max-w-[1200px] mx-auto px-5 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center relative">
          <div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5"
              style={{ fontFamily: "Chewy, system-ui", color: BRAND.forest }}
            >
              Your venue. <br />
              Their dog. <br />
              <span style={{ color: BRAND.terracotta }}>Your income.</span>
            </h1>
            <p className="text-base sm:text-lg text-stone-700 max-w-xl mb-8 leading-relaxed">
              Add a self-service dog wash and turn dog-owning customers into a hands-off revenue stream.
            </p>
            <button
              onClick={scrollToForm}
              data-testid="hero-cta"
              className="inline-flex items-center gap-2 text-white text-lg px-7 py-4 rounded-md hover:opacity-90 transition-opacity"
              style={{ background: BRAND.terracotta, boxShadow: "0 10px 30px rgba(224,120,86,0.35)" }}
            >
              Get the full details <ArrowDown className="w-5 h-5" />
            </button>
            <p className="text-xs text-stone-500 mt-3">
              No obligation. No hard sell. Just the numbers.
            </p>

            {/* Hero stat chips — space + revenue scenarios */}
            <div className="mt-8 max-w-md space-y-3" data-testid="hero-stats">
              <div
                className="rounded-2xl p-4 border flex items-center gap-3"
                style={{ background: "rgba(255,255,255,0.6)", borderColor: BRAND.sage }}
                data-testid="hero-stat-space"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: BRAND.forest, color: BRAND.cream }}
                >
                  <Ruler className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div
                    className="text-xl sm:text-2xl leading-none whitespace-nowrap"
                    style={{ fontFamily: "Chewy, system-ui", color: BRAND.forest }}
                  >
                    Fits in ~2m²
                  </div>
                  <div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-stone-500 mt-1">
                    Indoor or sheltered outdoor
                  </div>
                </div>
              </div>

              <div
                className="rounded-2xl p-4"
                style={{
                  background: BRAND.terracotta,
                  color: BRAND.cream,
                  boxShadow: "0 12px 30px rgba(224,120,86,0.28)",
                }}
                data-testid="hero-stat-revenue"
              >
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-[10px] sm:text-[11px] uppercase tracking-widest" style={{ color: "#FFE7DA" }}>
                    Net revenue potential
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { d: 5, net: "£1,365" },
                    { d: 10, net: "£2,730" },
                    { d: 15, net: "£4,095" },
                  ].map((s, i) => (
                    <div
                      key={i}
                      className="rounded-lg px-2 py-2 text-center"
                      style={{ background: "rgba(255,255,255,0.12)" }}
                      data-testid={`hero-scenario-${s.d}`}
                    >
                      <div className="text-[10px] uppercase tracking-widest whitespace-nowrap" style={{ color: "#FFE7DA" }}>
                        {s.d} washes/day
                      </div>
                      <div
                        className="text-lg sm:text-xl leading-none mt-1 whitespace-nowrap"
                        style={{ fontFamily: "Chewy, system-ui" }}
                      >
                        {s.net}
                      </div>
                      <div className="text-[9px] uppercase tracking-widest mt-1" style={{ color: "#FFE7DA", opacity: 0.85 }}>
                        /mo
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="relative">
            {/* Matted gallery-frame — cream mat + sage hairline + double shadow */}
            <div
              className="relative mx-auto p-3 sm:p-4"
              style={{
                maxWidth: "580px",
                background: BRAND.cream,
                borderRadius: "1.75rem",
                border: `1px solid ${BRAND.sage}`,
                boxShadow:
                  "0 1px 0 rgba(255,255,255,0.9) inset, 0 30px 80px rgba(17,66,50,0.35), 0 0 0 1px rgba(17,66,50,0.05)",
              }}
              data-testid="hero-image-frame"
            >
              <div
                className="overflow-hidden mx-auto"
                style={{
                  aspectRatio: "6 / 5",
                  borderRadius: "1.25rem",
                  boxShadow: "inset 0 0 0 1px rgba(17,66,50,0.12)",
                }}
              >
                <img
                  src="/images/machine-hero.jpg"
                  alt="Messy Pups self-service dog wash machine"
                  className="w-full h-full object-cover block"
                  data-testid="sales-hero-machine"
                />
              </div>
              {/* Floating "footprint" badge on the machine card */}
              <div
                className="hidden sm:flex absolute bottom-7 left-7 items-center gap-2 px-3 py-2 rounded-full"
                style={{
                  background: "rgba(17,66,50,0.85)",
                  backdropFilter: "blur(6px)",
                  color: BRAND.cream,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                }}
                data-testid="hero-floating-footprint"
              >
                <Ruler className="w-4 h-4" style={{ color: BRAND.terracotta }} />
                <span className="text-xs uppercase tracking-widest" style={{ letterSpacing: "0.15em" }}>
                  Full footprint · 2m²
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2 — THE OPPORTUNITY */}
      <section className="max-w-[1200px] mx-auto px-5 py-16 lg:py-20">
        <div className="text-[11px] uppercase tracking-[0.25em] text-stone-500 mb-3">
          The opportunity
        </div>
        <h2 className="text-3xl lg:text-4xl leading-tight mb-4" style={{ fontFamily: "Chewy, system-ui", color: BRAND.forest }}>
          Dogs are big business. <br className="hidden sm:block" />
          Are you getting your share?
        </h2>
        <p className="text-stone-700 max-w-2xl leading-relaxed mb-10">
          Dog owners are loyal, they spend, and they seek out venues that welcome their four-legged
          friends. A self-service dog wash puts your venue on their map.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="opportunity-stats">
          {[
            { value: "13m", label: "Dogs in the UK", body: "One in three households." },
            { value: "£+", label: "Higher spend per visit", body: "Dog owners linger — food, drink, browse." },
            { value: "Fast", label: "Growing venue add-on", body: "Self-service washes are one of the fastest-growing amenities." },
            { value: "< £1", label: "Running cost per wash", body: "Water, electricity, consumables — pennies each." },
          ].map((s, i) => (
            <div
              key={i}
              className="rounded-2xl p-6 border h-full"
              style={{ background: BRAND.bone, borderColor: BRAND.sage }}
              data-testid={`stat-${i}`}
            >
              <div
                className="text-5xl leading-none mb-2"
                style={{ fontFamily: "Chewy, system-ui", color: BRAND.terracotta }}
              >
                {s.value}
              </div>
              <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: BRAND.forest, fontWeight: 700 }}>
                {s.label}
              </div>
              <p className="text-sm text-stone-700 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
        <div
          className="mt-6 rounded-2xl p-6 lg:p-8"
          style={{ background: BRAND.forest, color: BRAND.cream }}
          data-testid="revenue-band"
        >
          <div className="grid md:grid-cols-2 gap-4 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: BRAND.sage, fontWeight: 700 }}>
                Revenue potential
              </div>
              <div className="text-2xl lg:text-3xl leading-tight" style={{ fontFamily: "Chewy, system-ui" }}>
                5 washes a day is meaningful income. <br />
                10 is serious. 15 is life-changing.
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.08)" }}>
                <div className="text-xs uppercase tracking-wider" style={{ color: BRAND.sage }}>5 / day</div>
                <div className="text-2xl lg:text-3xl mt-1" style={{ fontFamily: "Chewy, system-ui", color: BRAND.cream }}>
                  ~£1,365
                </div>
                <div className="text-xs mt-1" style={{ color: "#B7C3BB" }}>net / mo*</div>
              </div>
              <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.08)" }}>
                <div className="text-xs uppercase tracking-wider" style={{ color: BRAND.sage }}>10 / day</div>
                <div className="text-2xl lg:text-3xl mt-1" style={{ fontFamily: "Chewy, system-ui", color: BRAND.cream }}>
                  ~£2,730
                </div>
                <div className="text-xs mt-1" style={{ color: "#B7C3BB" }}>net / mo*</div>
              </div>
              <div
                className="rounded-xl p-4 border"
                style={{ background: "rgba(224,120,86,0.15)", borderColor: BRAND.terracotta }}
                data-testid="revenue-band-15"
              >
                <div className="text-xs uppercase tracking-wider" style={{ color: BRAND.terracotta, fontWeight: 700 }}>
                  15 / day
                </div>
                <div className="text-2xl lg:text-3xl mt-1" style={{ fontFamily: "Chewy, system-ui", color: BRAND.terracotta }}>
                  ~£4,095
                </div>
                <div className="text-xs mt-1" style={{ color: "#B7C3BB" }}>net / mo*</div>
              </div>
            </div>
          </div>
          <div className="text-xs mt-4" style={{ color: "#B7C3BB" }}>
            * £10 per wash gross, ~£0.90 running cost per wash, 30 days.
          </div>
        </div>
      </section>

      {/* 3 — HOW IT WORKS FOR YOU */}
      <section className="py-16 lg:py-20" style={{ background: BRAND.bone }}>
        <div className="max-w-[1200px] mx-auto px-5">
          <div className="text-[11px] uppercase tracking-[0.25em] text-stone-500 mb-3">
            How it works for you
          </div>
          <h2 className="text-3xl lg:text-4xl leading-tight mb-10" style={{ fontFamily: "Chewy, system-ui", color: BRAND.forest }}>
            Three simple steps. Then the machine takes over.
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                num: "01",
                title: "You install it — it's easy",
                body: "Plug-and-play: standard mains water, power and drainage. Detailed setup guide included. Most venues are up and running in a day.",
              },
              {
                num: "02",
                title: "Your customers use it",
                body: "Fully self-service, card payment, no staff involvement. Dog owners tap, wash, dry and leave.",
              },
              {
                num: "03",
                title: "You earn",
                body: "Revenue goes straight to you. Machine runs itself, 24/7 — every weekend, every bank holiday.",
              },
            ].map((s, i) => (
              <div
                key={i}
                className="rounded-2xl p-6 lg:p-8 bg-white relative border border-stone-200"
                data-testid={`how-step-${i}`}
              >
                <div
                  className="text-6xl leading-none mb-4 opacity-25"
                  style={{ fontFamily: "Chewy, system-ui", color: BRAND.terracotta }}
                >
                  {s.num}
                </div>
                <div className="text-2xl mb-2" style={{ fontFamily: "Chewy, system-ui", color: BRAND.forest }}>
                  {s.title}
                </div>
                <p className="text-stone-700 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — WHY MESSY PUPS */}
      <section className="max-w-[1200px] mx-auto px-5 py-16 lg:py-20">
        <div className="text-[11px] uppercase tracking-[0.25em] text-stone-500 mb-3">
          Why Messy Pups
        </div>
        <h2 className="text-3xl lg:text-4xl leading-tight mb-10" style={{ fontFamily: "Chewy, system-ui", color: BRAND.forest }}>
          Three things that set us apart.
        </h2>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            {
              icon: Sparkles,
              title: "It looks the part",
              body:
                "Most dog wash machines look like industrial equipment. Ours looks like it belongs. Wood-effect panels, mint-green branding, touchscreen — your customers will stop and take photos.",
            },
            {
              icon: Users,
              title: "It brings people in",
              body:
                "Dog owners will seek out your venue specifically because of the machine. Then they stay for food, drinks or a browse. Every wash is extra footfall you didn't have before — and every extra visit is a chance for extra spend at your tills.",
            },
            {
              icon: UserX,
              title: "Almost zero hassle",
              body:
                "Self-service, card-only. Keep it topped up with shampoo and conditioner — a five-minute task every week or two. No rotas. No training. Just income.",
            },
          ].map((u, i) => (
            <div
              key={i}
              className="rounded-2xl p-6 lg:p-8 border"
              style={{ background: BRAND.bone, borderColor: BRAND.sage }}
              data-testid={`usp-card-${i}`}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                style={{ background: BRAND.terracotta, color: BRAND.cream }}
              >
                <u.icon className="w-6 h-6" />
              </div>
              <div className="text-2xl mb-2" style={{ fontFamily: "Chewy, system-ui", color: BRAND.forest }}>
                {u.title}
              </div>
              <p className="text-stone-700 leading-relaxed">{u.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5 — BUY OR LEASE */}
      <section className="py-16 lg:py-20" style={{ background: BRAND.forest, color: BRAND.cream }}>
        <div className="max-w-[1200px] mx-auto px-5">
          <div className="text-[11px] uppercase tracking-[0.25em] mb-3" style={{ color: BRAND.sage }}>
            Buy or lease
          </div>
          <h2 className="text-3xl lg:text-4xl leading-tight mb-3" style={{ fontFamily: "Chewy, system-ui" }}>
            Own it or lease it — your choice.
          </h2>
          <p className="max-w-2xl mb-10 leading-relaxed" style={{ color: "#D6DED7" }}>
            Two clean ways in. Both include installation, setup and ongoing support.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            <div
              className="rounded-2xl p-6 lg:p-8"
              style={{ background: "rgba(255,255,255,0.06)", borderTop: `4px solid ${BRAND.sage}` }}
              data-testid="option-lease"
            >
              <Wallet className="w-8 h-8 mb-4" style={{ color: BRAND.sage }} />
              <div className="text-3xl mb-3" style={{ fontFamily: "Chewy, system-ui" }}>
                Lease
              </div>
              <div className="flex items-baseline gap-2 mb-4" data-testid="lease-price">
                <div
                  className="text-5xl lg:text-6xl leading-none"
                  style={{ fontFamily: "Chewy, system-ui", color: BRAND.cream }}
                >
                  £255
                </div>
                <div className="text-sm uppercase tracking-widest" style={{ color: BRAND.sage, fontWeight: 700 }}>
                  / month
                </div>
              </div>
              <p className="leading-relaxed mb-4" style={{ color: "#D6DED7" }}>
                Spread the cost. Machine installed, maintained and supported. A fixed monthly
                cost that a handful of washes a day will cover — the rest is profit.
              </p>
              <ul className="space-y-2 text-sm" style={{ color: "#E7ECE8" }}>
                <li>· Low upfront outlay</li>
                <li>· Fixed monthly figure</li>
              </ul>
            </div>
            <div
              className="rounded-2xl p-6 lg:p-8 relative"
              style={{ background: BRAND.cream, color: BRAND.ink, borderTop: `4px solid ${BRAND.terracotta}` }}
              data-testid="option-purchase"
            >
              <PoundSterling className="w-8 h-8 mb-4" style={{ color: BRAND.terracottaDeep }} />
              <div className="text-3xl mb-3" style={{ fontFamily: "Chewy, system-ui", color: BRAND.forest }}>
                Purchase
              </div>
              <div className="flex items-baseline gap-2 mb-4" data-testid="purchase-price">
                <div
                  className="text-5xl lg:text-6xl leading-none"
                  style={{ fontFamily: "Chewy, system-ui", color: BRAND.terracotta }}
                >
                  £10,995
                </div>
                <div className="text-sm uppercase tracking-widest" style={{ color: BRAND.terracottaDeep, fontWeight: 700 }}>
                  one-off
                </div>
              </div>
              <p className="text-stone-700 leading-relaxed mb-4">
                Own the machine outright. Maximum long-term return. Full installation
                and 1-year manufacturer warranty included.
              </p>
              <ul className="space-y-2 text-sm text-stone-800">
                <li>· One-off cost, no monthly fees</li>
                <li>· Best long-term margin</li>
                <li>· 1-yr manufacturer warranty</li>
              </ul>
            </div>
          </div>
          <p className="mt-8 text-center" style={{ color: "#D6DED7" }}>
            <button
              onClick={scrollToForm}
              className="underline hover:opacity-90"
              data-testid="options-cta"
            >
              Talk to us about which option suits your venue best →
            </button>
          </p>
        </div>
      </section>

      {/* 6 — PERFECT FOR */}
      <section className="max-w-[1200px] mx-auto px-5 py-16 lg:py-20">
        <div className="text-[11px] uppercase tracking-[0.25em] text-stone-500 mb-3">
          Perfect for
        </div>
        <h2 className="text-3xl lg:text-4xl leading-tight mb-10" style={{ fontFamily: "Chewy, system-ui", color: BRAND.forest }}>
          Anywhere dog owners already go.
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: Beer, label: "Pubs & gastropubs" },
            { icon: Sprout, label: "Farm shops" },
            { icon: Trees, label: "Garden centres" },
            { icon: Trees, label: "Country parks" },
            { icon: Tent, label: "Holiday parks" },
            { icon: ShoppingBag, label: "Pet shops" },
            { icon: Tent, label: "Campsites" },
            { icon: Dumbbell, label: "Sports clubs" },
          ].map((v, i) => (
            <div
              key={i}
              className="rounded-xl p-4 flex flex-col items-center text-center gap-2 border"
              style={{ background: BRAND.bone, borderColor: BRAND.sage }}
              data-testid={`venue-tile-${i}`}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: BRAND.forest, color: BRAND.cream }}
              >
                <v.icon className="w-5 h-5" />
              </div>
              <div className="text-sm text-stone-800">{v.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 7 — ENQUIRY */}
      <section id="enquire" className="py-16 lg:py-24" style={{ background: BRAND.bone }} data-testid="enquiry-section">
        <div className="max-w-[900px] mx-auto px-5">
          <div className="text-[11px] uppercase tracking-[0.25em] text-stone-500 mb-3">
            Enquiry
          </div>
          <h2 className="text-3xl lg:text-4xl leading-tight mb-3" style={{ fontFamily: "Chewy, system-ui", color: BRAND.forest }}>
            Sound interesting? Let's talk.
          </h2>
          <p className="text-stone-700 leading-relaxed max-w-2xl mb-8">
            No obligation. We'll walk you through everything, give you the real numbers for your type of
            venue, and answer any questions. No pressure, ever.
          </p>

          {sent ? (
            <div
              className="rounded-2xl p-8 lg:p-10 text-center bg-white border"
              style={{ borderColor: BRAND.sage }}
              data-testid="enquiry-success"
            >
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: BRAND.terracotta }} />
              <div className="text-3xl mb-2" style={{ fontFamily: "Chewy, system-ui", color: BRAND.forest }}>
                Thanks — enquiry received.
              </div>
              <p className="text-stone-700 max-w-md mx-auto">
                We've logged your details and Sarah will be in touch within 1–2 working days.
                If it's urgent, email <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="rounded-2xl p-6 lg:p-8 bg-white border grid gap-4"
              style={{ borderColor: BRAND.sage }}
              data-testid="enquiry-form"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="e-name" className="text-xs uppercase tracking-wider text-stone-600 mb-1 block">
                    Your name *
                  </Label>
                  <Input
                    id="e-name"
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    data-testid="enquiry-name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="e-biz" className="text-xs uppercase tracking-wider text-stone-600 mb-1 block">
                    Business name *
                  </Label>
                  <Input
                    id="e-biz"
                    value={form.business}
                    onChange={(e) => setField("business", e.target.value)}
                    data-testid="enquiry-business"
                    required
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-600 mb-1 block">
                    Type of venue *
                  </Label>
                  <Select value={form.venue_type} onValueChange={(v) => setField("venue_type", v)}>
                    <SelectTrigger data-testid="enquiry-venue-trigger">
                      <SelectValue placeholder="Choose one" />
                    </SelectTrigger>
                    <SelectContent>
                      {VENUE_TYPES.map((v) => (
                        <SelectItem key={v} value={v} data-testid={`venue-option-${v}`}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="e-phone" className="text-xs uppercase tracking-wider text-stone-600 mb-1 block">
                    Phone
                  </Label>
                  <Input
                    id="e-phone"
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    data-testid="enquiry-phone"
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="e-email" className="text-xs uppercase tracking-wider text-stone-600 mb-1 block">
                  Email *
                </Label>
                <Input
                  id="e-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  data-testid="enquiry-email"
                  required
                />
              </div>

              <div>
                <Label htmlFor="e-msg" className="text-xs uppercase tracking-wider text-stone-600 mb-1 block">
                  Message
                </Label>
                <Textarea
                  id="e-msg"
                  rows={4}
                  value={form.message}
                  onChange={(e) => setField("message", e.target.value)}
                  data-testid="enquiry-message"
                  placeholder="Anything you'd like us to know? (optional)"
                />
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
                <div className="text-xs text-stone-500 flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5" /> Prefer to phone? Email <a href={`mailto:${CONTACT_EMAIL}`} className="underline">Sarah</a> and we'll ring you.
                </div>
                <Button
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  data-testid="enquiry-submit"
                  className="text-white hover:opacity-90"
                  style={{ background: BRAND.terracotta }}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5 mr-2" /> Send my enquiry
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="py-8 text-center text-xs text-stone-500 border-t border-stone-200">
        <div>
          Messy Pups · Kingston upon Thames · <a href={`mailto:${CONTACT_EMAIL}`} className="underline" data-testid="footer-email">{CONTACT_EMAIL}</a>
        </div>
        <div className="mt-2">
          <Link to="/advert" className="inline-flex items-center gap-1.5 hover:underline" data-testid="footer-advert-link">
            <Download className="w-3.5 h-3.5" /> Download social advert pack
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default SalesLanding;
